import {
  StackProjections,
  getBottomWallStackId,
  getCountertopWallStackId,
  getPositionedStack,
  getTopWallStackId,
} from 'jms-domain'
import type { Model, StackItem } from 'jms-domain'
import { BottomWallItem, CountertopWallItem, TopWallItem } from 'jms-domain/src/__generated/model/index.js'
import type { PropsWithChildren } from 'react'
import { useEffect } from 'react'
import { match } from 'ts-pattern'

import { draggableComponent, isDomainEntityId } from '@salsita-ai/platform-project-sdk/domain'

import { configuratorInterface } from '../systems/configurator-interface.js'
import { generateCountertopRepositioningUpdates } from './utils/countertop-repositioning.js'

export const DndTransformerComponent = ({ children }: PropsWithChildren) => {
  const domainContext = configuratorInterface.useDomain()

  useEffect(() => {
    const transformerRegistration = configuratorInterface.transformerBuilder.when
      .component([draggableComponent])
      .modified.then.overrideMatchingUpdate(ctx => {
        const draggedEntityId = ctx.update.entityId

        if (!isDomainEntityId(draggedEntityId)) {
          throw new Error(
            `Dragged entity id is not a valid domain entity id, this should not happen! Only DomainEntities can be dragged!`
          )
        }

        const committedState = ctx.getOldState()

        const stackProjections = new StackProjections(domainContext.componentReader)

        const surfaceKey = ctx.update.metadata?.['surfaceKey'] as string | undefined

        if (surfaceKey === undefined) {
          throw new Error(
            `Surface key is not a string, this should not happen! Make sure the dragged item is a valid stack item and is dragging surfaces keys match the available stacks!`
          )
        }

        const getStackKey = match(draggedEntityId)
          .when(TopWallItem.isId, () => getTopWallStackId)
          .when(BottomWallItem.isId, () => getBottomWallStackId)
          .when(CountertopWallItem.isId, () => getCountertopWallStackId)
          .otherwise(() => {
            throw new Error('Invalid wall item type!')
          })

        const stackKey = getStackKey(surfaceKey)

        const draggedStackItem = stackProjections.getStackItem(
          // TODO: type cast is temporary, this will not be problem in sdk
          draggedEntityId as Model.AnyDomainEntity['id'],
          committedState
        )

        const movedDraggedItem = {
          ...draggedStackItem,
          x: ctx.update.writableState.x,
          stackKey,
        } satisfies StackItem

        const stack = stackProjections.getStack(stackKey, committedState)
        const $wall = ctx.domainContext.domain.get$Entity(surfaceKey as Model.Wall['id'], committedState)

        // Get whitelisted spaces for CountertopWallItems
        const whitelistedSpaces = match(draggedEntityId)
          .when(CountertopWallItem.isId, () => $wall.$continuousCountertopSpaces)
          .otherwise(() => undefined)

        const repositionedStack = getPositionedStack(stack, movedDraggedItem, whitelistedSpaces)

        const stackUpdates = repositionedStack.map(item => {
          return configuratorInterface.updates.ComponentModification({
            componentInstance: item.componentInstance,
            updateTag: 'stackDnDTransformer',
            writableState: {
              x: item.x,
              stackKey,
            },
          })
        })

        // If a BottomWallItem was moved, check and reposition any invalid CountertopWallItems
        const countertopUpdates = match(draggedEntityId)
          .when(BottomWallItem.isId, () => {
            // Get the repositioned bottom items from the stack
            const repositionedBottomItems = repositionedStack.map(item => {
              const $entity = ctx.domainContext.domain.get$Entity(
                item.componentInstance.entityId as Model.BottomWallItem['id'],
                committedState
              )
              return {
                x: item.x,
                $width: $entity.$width,
                $hasWorkSurface: $entity.$hasWorkSurface,
              }
            })

            return generateCountertopRepositioningUpdates(
              $wall,
              repositionedBottomItems,
              stackProjections,
              committedState
            )
          })
          .otherwise(() => [])

        return [...stackUpdates, ...countertopUpdates]
      })
      .build('dnd-transformer')
      .register(domainContext)

    const unregister = domainContext.storeSystem.registerTransformer(
      // TODO: fix SDK bug
      1000000,
      transformerRegistration.transformer,
      transformerRegistration.name!
    )

    return () => {
      unregister()
    }
  }, [domainContext])

  return <>{children}</>
}
